import React, { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { parseDay, dayParts } from '../../../utils/columnFilterTypes';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

// Group raw cell values into Year > Month > Day. A day leaf can hold several raw values (a column of
// timestamps has many per day); ticking a node ticks every raw value under it, so the filter stays a
// plain value list. Blanks and anything that does not parse get their own rows.
export const buildDateTree = (values) => {
  const years = new Map();
  const blanks = [];
  const other = [];
  for (const v of values) {
    if (v === '') { blanks.push(v); continue; }
    const day = parseDay(v);
    if (day === null) { other.push(v); continue; }
    const { y, m, d } = dayParts(day);
    if (!years.has(y)) years.set(y, new Map());
    const months = years.get(y);
    if (!months.has(m)) months.set(m, new Map());
    const days = months.get(m);
    if (!days.has(d)) days.set(d, []);
    days.get(d).push(v);
  }
  const tree = [...years.entries()].sort((a, b) => a[0] - b[0]).map(([y, months]) => ({
    key: `${y}`, label: `${y}`,
    children: [...months.entries()].sort((a, b) => a[0] - b[0]).map(([m, days]) => ({
      key: `${y}-${m}`, label: MONTHS[m - 1],
      children: [...days.entries()].sort((a, b) => a[0] - b[0]).map(([d, raw]) => ({
        key: `${y}-${m}-${d}`, label: `${d}`, values: raw,
      })),
    })),
  }));
  const valuesOf = (node) => node.values || node.children.flatMap(valuesOf);
  tree.forEach(y => { y.values = valuesOf(y); y.children.forEach(m => { m.values = valuesOf(m); }); });
  return { tree, blanks, other };
};

/** Tri-state: 'all' | 'some' | 'none' of these values ticked. */
const stateOf = (values, selected) => {
  let hit = 0;
  for (const v of values) if (selected.has(v)) hit++;
  return hit === 0 ? 'none' : hit === values.length ? 'all' : 'some';
};

const TreeCheckbox = ({ state, onChange, label }) => (
  <input
    type="checkbox"
    className="stui-checkbox-input"
    checked={state === 'all'}
    ref={el => { if (el) el.indeterminate = state === 'some'; }}
    onChange={onChange}
    aria-label={label}
  />
);

/**
 * Year > Month > Day value list for date columns (Excel's date filter tree). Ticks are the raw cell
 * values; `onToggle(values, checked)` adds or removes a whole node's values. Collapsed by default
 * except a lone year; everything expands while a search is narrowing the list.
 */
const DateValueTree = ({ values, selected, onToggle, searching = false }) => {
  const { tree, blanks, other } = useMemo(() => buildDateTree(values), [values]);
  const [expanded, setExpanded] = useState(() => new Set(tree.length === 1 ? [tree[0].key] : []));
  const isOpen = (key) => searching || expanded.has(key);
  const toggleOpen = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const row = (node, level, hasChildren) => {
    const state = stateOf(node.values, selected);
    return (
      <div key={node.key} className={`stui-filter-option stui-filter-tree-node${level > 1 ? ` l${level}` : ''}`}>
        <button
          type="button"
          className={`stui-filter-tree-toggle${hasChildren ? '' : ' leaf'}`}
          onClick={() => { if (hasChildren) toggleOpen(node.key); }}
          aria-label={hasChildren ? (isOpen(node.key) ? `Collapse ${node.label}` : `Expand ${node.label}`) : undefined}
          tabIndex={hasChildren ? 0 : -1}
        >
          {hasChildren && (isOpen(node.key) ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </button>
        <TreeCheckbox state={state} label={node.label} onChange={() => onToggle(node.values, state !== 'all')} />
        <span onClick={() => onToggle(node.values, state !== 'all')}>{node.label}</span>
      </div>
    );
  };

  const flatRow = (key, label, rowValues) => {
    const state = stateOf(rowValues, selected);
    return (
      <div key={key} className="stui-filter-option stui-filter-tree-node">
        <span className="stui-filter-tree-toggle leaf" />
        <TreeCheckbox state={state} label={label} onChange={() => onToggle(rowValues, state !== 'all')} />
        <span onClick={() => onToggle(rowValues, state !== 'all')}>{label}</span>
      </div>
    );
  };

  return (
    <div className="stui-filter-tree">
      {tree.map(y => (
        <React.Fragment key={y.key}>
          {row(y, 1, true)}
          {isOpen(y.key) && y.children.map(m => (
            <React.Fragment key={m.key}>
              {row(m, 2, true)}
              {isOpen(m.key) && m.children.map(d => row(d, 3, false))}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
      {other.length > 0 && flatRow('(other)', `(Other: ${other.length})`, other)}
      {blanks.length > 0 && flatRow('(blanks)', '(Blanks)', blanks)}
    </div>
  );
};

export default DateValueTree;
